import { Component, Emit, Ref, Vue } from 'vue-property-decorator'
import VtInput from '@/components/common/VtInput.vue'
import {
    initialLoginLayoutLoginState,
    LoginLayoutLoginState
} from '@/views/auth/components/login/login-layout-login/login-layout-login.model'
import { auth } from '@/api/auth'
import sessionStore from '@/session/sessionStore.js'
import moduleResource, { MODULES } from '@/api/resources/ModuleResource'
import { ModuleOperation } from '@/api/operations/models/module-operation'
import apiExceptionType from '@/utils/apiExceptionType'
import { store } from '@/store/store'
import { router } from '@/router'
import { UserType } from '@/api/resources/common/initialization/common-initialization.dto'
import { HelperUtils } from '@/shared/util/helper-utils'

@Component
export default class LoginLayoutLogin extends Vue {
    @Ref() usernameInput!: typeof VtInput

    get hasError(): boolean {
        return !!this.state.error
    }

    state: LoginLayoutLoginState
    constructor() {
        super()
        this.state = initialLoginLayoutLoginState()
    }

    mounted() {
        (this.usernameInput as any).focus()
    }

    @Emit('show-recovery')
    private _showRecovery(): void {}

    @Emit('show-change-password')
    private _showChangePassword(): void {}

    togglePassword(): void {
        this.state.showPassword = !this.state.showPassword
    }

    async logIn(): Promise<void> {
        if (!this.state.username && this.state.password) {
            this.state.usernameInError = true
            this.state.passwordInError = false
        } else if (!this.state.password && this.state.username) {
            this.state.usernameInError = false
            this.state.passwordInError = true
        } else if (!this.state.password && !this.state.username) {
            this.state.usernameInError = true
            this.state.passwordInError = true
        } else if (this.state.username && this.state.password) {
            try {
                this.state.busy = true
                this.state.error = ''
                this.state.usernameInError = false
                this.state.passwordInError = false

                await auth.logIn(this.state.username, this.state.password)
                await this._logInOk()
            } catch (e) {
                if (e.response) {
                    this._logInFailed(e.response)
                } else {
                    console.error(e)
                }

                this.state.usernameInError = true
                this.state.passwordInError = true
            } finally {
                this.state.busy = false
            }
        }
    }

    private async _logInOk(): Promise<void> {
        if (auth.isPasswordSingleUse()) {
            this._showChangePassword()
            return
        }

        // Validations
        if (!store.state.laboratoriesConfiguration.length) {
            // No operation configured to any laboratory
            this.state.error = this.$tc('userConfiguration.noOperationsError')
        } else if (!store.state.modules.length) {
            // No access to any laboratory
            this.state.error = this.$tc('userConfiguration.noModulesError')
        }
        if (this.state.error) {
            auth.clearSession()
            return
        }

        let moduleCode = MODULES[ModuleOperation.DASHBOARD].moduleCode
        if (store.state.userInformation?.userType === UserType.PERSONNEL) {
            const userDefaultLabModule =
                store.state.defaultAccess?.defaultLabModules.find(
                    (labModule: any) => labModule.laboratory.labId === store.state.labId)
            if (userDefaultLabModule && HelperUtils.hasModuleAccess(userDefaultLabModule.moduleCode)) {
                moduleCode = userDefaultLabModule.moduleCode
            }
        }
        const routeName = moduleResource.getModuleRouteNameFromModuleCode(moduleCode)
        if (routeName) {
            await router.push({ name: routeName })
            store.commit('updateShowGreetMessageFlag', true)
        }
    }

    private _logInFailed(response: any): void {
        const data = response.data
        if (data) {
            switch (data.code) {
            case apiExceptionType.USER_CREDENTIALS_EXPIRED:
                this.state.error = this.$tc('login.error.credentialsExpired')
                break
            case apiExceptionType.BAD_CREDENTIALS:
                this.state.error = this.$tc('login.error.badCredentials')
                break
            case apiExceptionType.USER_ACCOUNT_LOCKED:
                this.state.error = this.$tc('login.error.userAccountLocked')
                break
            default:
                this.$_showErrorModal()
                break
            }
        } else {
            this.$_showErrorModal()
        }
    }

    async goRecovery(): Promise<void> {
        const [error, globalExternalAuthentication] = await auth.isGlobalExternalAuthentication(sessionStore.getLocale())
        if (error) {
            return
        }

        if (globalExternalAuthentication.globalExternalAuthentication) {
            this.state.error = globalExternalAuthentication.message
        } else {
            this._showRecovery()
        }
    }
}
